import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  ArrowLeft,
  Clock, 
  ShieldCheck, 
  ThermometerSun,
  FileText,
  Activity,
  CheckCircle,
  Download
} from "lucide-react";
import PricingRequestModal from "@/components/PricingRequestModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const productsData = {
  "strep-a": {
    id: "strep-a",
    sku: "MRK250006",
    name: "Healgen Rapid Strep A Antigen Test",
    shortDescription: "CLIA-waived rapid immunoassay for qualitative detection of Strep A antigen from throat swab specimens.",
    description: "The Healgen Rapid Strep A Antigen Test is a CLIA-waived rapid immunoassay for the qualitative detection of Group A Streptococcal antigen from throat swab specimens. This point-of-care test provides reliable results in just 5 minutes, enabling healthcare professionals to make timely treatment decisions for patients presenting with signs and symptoms of pharyngitis.",
    intendedUse: "For the rapid, qualitative detection of Group A Streptococcal antigen in throat swab specimens from patients with signs and symptoms of pharyngitis. For professional in vitro diagnostic use only.",
    features: [
      "CLIA Waived for point-of-care use",
      "Results in 5 minutes",
      "96% sensitivity, 98% specificity",
      "25 tests per kit",
      "Throat swab specimen collection",
      "Room temperature storage (15-30°C)"
    ],
    specifications: {
      "Specimen Type": "Throat swab",
      "Results Time": "5 minutes",
      "Tests Per Kit": "25 tests",
      "Storage": "15-30°C (room temperature)",
      "Sensitivity": "96%",
      "Specificity": "98%",
      "Shelf Life": "24 months from manufacture"
    },
    regulatory: {
      status: "FDA 510(k) Cleared",
      clia: "CLIA Waived",
      badge: "CLIA Waived"
    },
    images: {
      hero: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/36hklcrv_healgen_strep_a.png",
      secondary: "https://healgen.com/wp-content/uploads/2025/10/Strep-A.png"
    }
  },
  "covid-flu": {
    id: "covid-flu",
    sku: "MRK240016",
    name: "Healgen Rapid Check COVID-19 / Flu A & B Antigen Combo Test",
    shortDescription: "Combined detection of SARS-CoV-2, Influenza A, and Influenza B antigens in a single test.",
    description: "The Healgen Rapid Check COVID-19 / Flu A & B Antigen Combo Test provides differential detection of SARS-CoV-2, Influenza A, and Influenza B antigens from a single nasopharyngeal swab specimen. This 3-in-1 test streamlines respiratory illness workup during peak seasons, reducing the need for multiple individual tests while maintaining high accuracy across all three targets.",
    intendedUse: "For the simultaneous qualitative detection and differentiation of SARS-CoV-2, Influenza A, and Influenza B viral nucleoprotein antigens in nasopharyngeal swab specimens from individuals suspected of respiratory viral infection. For professional in vitro diagnostic use only.",
    features: [
      "3-in-1 detection (COVID-19, Flu A, Flu B)",
      "Results in 15 minutes",
      "High accuracy for all three targets",
      "Nasopharyngeal swab specimen",
      "Streamlined respiratory season workflow",
      "Professional use in clinical settings"
    ],
    specifications: {
      "Specimen Type": "Nasopharyngeal swab",
      "Results Time": "15 minutes",
      "Tests Per Kit": "20 tests",
      "Storage": "2-30°C",
      "Targets": "SARS-CoV-2, Influenza A, Influenza B",
      "Shelf Life": "18 months from manufacture"
    },
    regulatory: {
      status: "FDA Emergency Use Authorization (EUA)",
      clia: "Professional Use",
      badge: "EUA Authorized"
    },
    images: {
      hero: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/bae9f7bt_healgen_covid_flu_pro.png",
      secondary: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/s27cf51w_healgen_covid_flu_home_use.png"
    }
  },
  "rsv": {
    id: "rsv",
    sku: "MRK240002",
    name: "Healgen Rapid Check RSV Antigen Test",
    shortDescription: "Rapid qualitative detection of Respiratory Syncytial Virus (RSV) antigen for clinical settings.",
    description: "The Healgen Rapid Check RSV Antigen Test provides rapid qualitative detection of Respiratory Syncytial Virus (RSV) antigen from nasopharyngeal swab specimens. Essential for pediatric and geriatric care settings where RSV poses significant risk, this test enables prompt diagnosis and appropriate patient management decisions.",
    intendedUse: "For the qualitative detection of Respiratory Syncytial Virus (RSV) antigen in nasopharyngeal swab specimens from patients with signs and symptoms of respiratory infection. For professional in vitro diagnostic use only.",
    features: [
      "Rapid RSV detection",
      "Results in 10 minutes",
      "Simple procedure",
      "Room temperature storage",
      "Reliable performance",
      "Nasopharyngeal swab specimen"
    ],
    specifications: {
      "Specimen Type": "Nasopharyngeal swab",
      "Results Time": "10 minutes",
      "Tests Per Kit": "25 tests",
      "Storage": "15-30°C (room temperature)",
      "Target": "RSV antigen",
      "Shelf Life": "24 months from manufacture"
    },
    regulatory: {
      status: "FDA 510(k) Cleared",
      clia: "Professional Use",
      badge: "FDA Cleared"
    },
    images: {
      hero: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/n2n838ro_healgen_rsv.png",
      secondary: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/n2n838ro_healgen_rsv.png"
    }
  }
};

export default function ProductDetailPage() {
  const { productId } = useParams();
  const product = productsData[productId];
  const [pricingModalOpen, setPricingModalOpen] = useState(false);

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-secondary mb-4">Product Not Found</h1>
          <p className="body-default mb-8">The requested product could not be found.</p>
          <Link to="/products">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Pricing Request Modal */}
      <PricingRequestModal open={pricingModalOpen} onOpenChange={setPricingModalOpen} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
          <nav className="breadcrumb" data-testid="product-breadcrumb">
            <Link to="/" className="hover:text-sky-600 transition-colors">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/products" className="hover:text-sky-600 transition-colors">Products</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="text-slate-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Detail */}
      <section className="bg-white section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div>
              <div className="aspect-square bg-slate-50 rounded-2xl overflow-hidden sticky top-24">
                <img 
                  src={product.images.hero}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-detail-image"
                />
              </div>
            </div>

            {/* Product Info */}
            <div>
              {/* Badges */}
              <div className="flex items-center gap-3 mb-4">
                <span className="regulatory-badge" data-testid="product-regulatory-badge">{product.regulatory.badge}</span>
                <span className="sku-badge" data-testid="product-sku">SKU: {product.sku}</span>
              </div>

              {/* Title */}
              <h1 className="heading-primary mb-4" data-testid="product-detail-name">
                {product.name}
              </h1>

              {/* Short Description */}
              <p className="body-large mb-8" data-testid="product-short-description">
                {product.shortDescription}
              </p>

              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <Clock className="w-5 h-5 text-sky-600" />
                  <div>
                    <div className="text-sm text-slate-500">Results Time</div>
                    <div className="font-semibold text-slate-900">{product.specifications["Results Time"]}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <ThermometerSun className="w-5 h-5 text-sky-600" />
                  <div>
                    <div className="text-sm text-slate-500">Storage</div>
                    <div className="font-semibold text-slate-900">Room Temp</div>
                  </div>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4 mb-6">
                <Button 
                  onClick={() => setPricingModalOpen(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base"
                  data-testid="product-request-pricing-btn"
                >
                  Request Pricing
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Link to="/contact" data-testid="product-contact-sales-btn">
                  <Button variant="outline" className="border-slate-300 px-8 py-6 text-base">
                    Contact Sales
                  </Button>
                </Link>
              </div>

              {/* Download Spec Sheet */}
              <div className="mb-8">
                <a 
                  href={`${BACKEND_URL}/api/products/${product.id}/spec-sheet`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-download-spec-btn"
                >
                  <Button variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50 w-full sm:w-auto">
                    <Download className="mr-2 w-4 h-4" />
                    Download Spec Sheet (PDF)
                  </Button>
                </a>
              </div>

              {/* Regulatory Status */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg mb-8">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <div>
                    <div className="font-semibold text-emerald-900">Regulatory Status</div>
                    <div className="text-sm text-emerald-700">{product.regulatory.status} • {product.regulatory.clia}</div>
                  </div>
                </div>
              </div>

              {/* Professional Use Disclaimer */}
              <div className="disclaimer-box">
                <Activity className="w-5 h-5 inline-block mr-2" />
                <span className="font-medium">For professional use only.</span> Not for consumer sale.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details Sections */}
      <section className="bg-slate-50 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Description */}
            <div className="lg:col-span-2 bg-white rounded-xl p-8 border border-slate-200">
              <h2 className="heading-tertiary mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-sky-600" />
                Product Overview
              </h2>
              <p className="body-default mb-6" data-testid="product-full-description">
                {product.description}
              </p>
              
              <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Intended Use
              </h3>
              <p className="body-default text-sm" data-testid="product-intended-use">
                {product.intendedUse}
              </p>
            </div>

            {/* Features */}
            <div className="bg-white rounded-xl p-8 border border-slate-200">
              <h2 className="heading-tertiary mb-4">Key Features</h2>
              <ul className="space-y-3">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3" data-testid={`product-feature-${idx}`}>
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="body-default text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Specifications Table */}
          <div className="mt-8 bg-white rounded-xl p-8 border border-slate-200">
            <h2 className="heading-tertiary mb-6">Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(product.specifications).map(([key, value]) => (
                <div key={key} className="p-4 bg-slate-50 rounded-lg" data-testid={`spec-${key.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="text-sm text-slate-500 mb-1">{key}</div>
                  <div className="font-semibold text-slate-900">{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Order {product.name}?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Contact our sales team for pricing, volume discounts, and ordering information.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setPricingModalOpen(true)}
              className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base"
              data-testid="product-cta-request-pricing"
            >
              Request Pricing
            </Button>
            <Link to="/products" data-testid="product-cta-browse-products">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-base">
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
